<template>
  <div class="confirm-overlay" @click.self="$emit('cancel')" @keydown="onKeydown">
    <div
      ref="card"
      class="confirm-card"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      aria-describedby="confirm-message"
      tabindex="-1"
    >
      <div class="confirm-icon" :class="'variant-' + variant" aria-hidden="true">{{ variant === "info" ? "i" : "!" }}</div>
      <h2 id="confirm-title" class="confirm-title">{{ title }}</h2>
      <p id="confirm-message" class="confirm-message">{{ message }}</p>
      <div class="confirm-actions">
        <button ref="cancel" type="button" class="confirm-btn ghost" @click="$emit('cancel')">{{ cancelLabel }}</button>
        <button ref="confirm" type="button" class="confirm-btn" :class="destructive ? 'danger' : 'primary'" @click="$emit('confirm')">
          {{ confirmLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Replaces the browser's own window.confirm(): a question with two answers, in the app's look.
// Emits "confirm" or "cancel" (the backdrop and Esc cancel too).
export default {
  name: "ConfirmDialog",
  props: {
    title: { type: String, required: true },
    message: { type: String, default: "" },
    confirmLabel: { type: String, default: "Aceptar" },
    cancelLabel: { type: String, default: "Cancelar" },
    // warning ("!") | info ("i")
    variant: { type: String, default: "warning" },
    // The confirm answer throws something away: red button, and focus starts on cancel so a stray Enter is safe.
    destructive: { type: Boolean, default: false },
  },
  mounted() {
    this.previousFocus = document.activeElement;
    this.$nextTick(() => {
      const button = this.destructive ? this.$refs.cancel : this.$refs.confirm;
      if (button) button.focus();
    });
  },
  beforeDestroy() {
    // Back to where the applicant was (e.g. the panel's close button), unless that's gone.
    const el = this.previousFocus;
    if (el && document.body.contains(el) && el.focus) el.focus();
  },
  methods: {
    // Handled here and stopped: the dialog may sit inside a panel that also closes on Esc.
    onKeydown(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        this.$emit("cancel");
      } else if (e.key === "Tab") {
        // Keeps Tab between the two buttons while the dialog is open.
        const { cancel, confirm } = this.$refs;
        const first = e.shiftKey ? confirm : cancel;
        const last = e.shiftKey ? cancel : confirm;
        if (document.activeElement === last || document.activeElement === this.$refs.card) {
          e.preventDefault();
          first.focus();
        }
      } else {
        e.stopPropagation();
      }
    },
  },
};
</script>

<style scoped>
.confirm-overlay {
  position: fixed;
  inset: 0;
  /* Above the portal's side panel and viewer, and the wizard's notice modals. */
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(22, 34, 61, 0.55);
}
.confirm-card {
  width: 100%;
  max-width: 420px;
  padding: 28px 26px 24px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 20px 50px -12px rgba(22, 34, 61, 0.45);
  text-align: center;
  outline: none;
}
.confirm-icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  /* Plain ASCII letter, not a Unicode symbol — renders the same everywhere. */
  font: 700 22px "IBM Plex Sans", sans-serif;
}
.confirm-icon.variant-warning {
  background: #ffedd5;
  color: #c2410c;
}
.confirm-icon.variant-info {
  background: #dcfce7;
  color: #15803d;
}
.confirm-title {
  margin: 0 0 10px;
  font: 600 18px/1.35 "IBM Plex Sans", sans-serif;
  color: #16223d;
}
.confirm-message {
  margin: 0 0 22px;
  font: 400 14px/1.6 "IBM Plex Sans", sans-serif;
  color: #3f434e;
}
.confirm-actions {
  display: flex;
  gap: 10px;
}
.confirm-btn {
  flex: 1;
  height: 44px;
  padding: 0 14px;
  border-radius: 9px;
  font: 600 14px "IBM Plex Sans", sans-serif;
  cursor: pointer;
}
.confirm-btn.ghost {
  border: 1px solid #c9c6bc;
  background: #fff;
  color: #16223d;
}
.confirm-btn.ghost:hover {
  background: #f9f8f5;
}
.confirm-btn.primary,
.confirm-btn.danger {
  border: none;
  color: #fff;
}
.confirm-btn.primary {
  background: #16223d;
}
.confirm-btn.primary:hover {
  background: #0f1830;
}
.confirm-btn.danger {
  background: #b91c1c;
}
.confirm-btn.danger:hover {
  background: #991b1b;
}
.confirm-btn:focus-visible {
  outline: 2px solid #c9963a;
  outline-offset: 2px;
}

@media (max-width: 420px) {
  .confirm-actions {
    flex-direction: column-reverse;
  }
}
</style>
